import styles from "../LivePlayer.module.scss"

import React, {
  FocusEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

import { playerService } from "@shared/services"

import { Icon } from "antd"
import classNames from "classnames"
import { Player } from "live/modules"
import { PlayerStatus } from "live/types"

import ChartContent from "chart/components/content/ChartContent"

import {
  ContentType,
  useContentType,
  useIsMiniPlayerEnabled,
  useSetIsMiniPlayerEnabled,
} from "../../../contexts/miniPlayer"
import { Header } from "../../header"
import FlashLivePlayer from "../FlashPlayer"
import SldpPlayer from "../SldpPlayer"
import {useSchedule} from "@shared/contexts";
import YouTubeContent from "./YoutubeContent";
import StockVodContent from "./StockVodContent";


interface Props {
  isFlash: boolean
  isFullMode: boolean
}

function LivePlayer({ isFlash, isFullMode }: Props) {
  const videoWrapRef = useRef<HTMLDivElement>(null)
  const ref = useRef<HTMLDivElement>(null)

  const [player, setPlayer] = useState<Player | undefined>()
  const [status, setStatus] = useState<PlayerStatus>("LOADING")
  const isMiniPlayerEnabled = useIsMiniPlayerEnabled()
  const handleMiniPlayer = useSetIsMiniPlayerEnabled()
  const { schedule } = useSchedule()
  const contentType = useContentType();

  const isPaidSchedule =
    !!schedule?.paymentInformation && schedule.paymentInformation === "PAID"

  const handlePlay = useCallback(
    (e: MouseEvent): void => {
      e.preventDefault()
      player!.play()
    },
    [player],
  )

  const handleFullMode = useCallback(() => {
    playerService.handleFullMode(videoWrapRef)
  }, [videoWrapRef])

  const isPlayButton = !(
    ["PLAYING", "ERROR", "LOADING"] as PlayerStatus[]
  ).includes(status)

  const handleClickPlayerSize = () => {
    if (isMiniPlayerEnabled) {
      handleMiniPlayer.inactive()
    }
  }

  return (
    <div className="playerWrapper" ref={ref}>
      {!isFullMode && player && <Header player={player} />}
      <div className={isMiniPlayerEnabled ? "miniPlayer" : "fullPlayer"} onClick={handleClickPlayerSize}>
        <article
          className={classNames(`vodB`, styles.video, {
            loaded: status !== "ERROR",
          })}
        >
          {isPlayButton && (
            <>
              <Icon
                type="caret-right"
                className={styles.icoPlay}
                onClick={handlePlay}
              />
              <div className={styles.dimming} onClick={handlePlay}></div>
            </>
          )}

          <div
            className={styles.videoWrap}
            ref={videoWrapRef}
            onDoubleClick={(e: MouseEvent) => {
              e.preventDefault()
              playerService.handleFullMode(videoWrapRef)
            }}
            onFocus={(e: FocusEvent) => e.preventDefault()}
          >
            {isFlash ? (
              <FlashLivePlayer
                player={player}
                setPlayer={setPlayer}
                handleFullMode={handleFullMode}
              />
            ) : (
              <SldpPlayer
                player={player}
                setPlayer={setPlayer}
                setStatus={setStatus}
                handleFullMode={handleFullMode}
              />
            )}
          </div>

          {status === "LOADING" && (
            <Icon type="loading" className={styles.icoLoading} />
          )}
        </article>
      </div>
      {isMiniPlayerEnabled && isPaidSchedule && (
        contentType === ContentType.chart ? (
          <div className={'chart'}>
            <ChartContent />
          </div>
        ) : contentType === ContentType.youtube ? (
          <>
            <YouTubeContent parentRef={ref} />
            <StockVodContent />
          </>
        ) : null
      )}
    </div>
  )
}

LivePlayer.FakePlayer = () => (
  <article className={classNames(`vodB`, styles.video)}>
    <div className={styles.videoWrap}>
      <video />
    </div>
  </article>
)

export default LivePlayer
